import { removeBlueAndUnderlineFromNotes, removeMniWhenApproved, removeNoteRoundBrackets, } from './helper';

export const normalizeRedactHtml = (html) => {
    let result = html || '';
    result = result?.replace(/background-color:\s*rgb\(0,\s*128,\s*0\)/g, 'background-color: #EAEAE8');
    result = result?.replace(/color:\s*rgb\(127,\s*255,\s*212\)/g, 'color: #434A54');
    result = result?.replace(
        /background-color:\s*(?:red|#ff0000|#f00|rgb\s*\(\s*255\s*,\s*0\s*,\s*0\s*\))/gi,
        'background-color: transparent',
    );
    result = result?.replace(
        /background:\s*(?:red|#ff0000|#f00|rgb\s*\(\s*255\s*,\s*0\s*,\s*0\s*\))/gi,
        'background: transparent',
    );
    result = result?.replace(
        /color:\s*(?:red|#ff0000|#f00|rgb\s*\(\s*255\s*,\s*0\s*,\s*0\s*\))/gi,
        'color: #434A54',
    );
    result = result?.replace(
        /color:\s*(?:white|#fff(?:fff)?|#ffffff|rgb\s*\(\s*255\s*,\s*255\s*,\s*255\s*\))/gi,
        'color: #434A54',
    );
    return result;
};

export const normalizeStandardHtml = (html) => {
    let result = html || '';
    result = result?.replace(
        /background-color:\s*(?:red|#ff0000|#f00|rgb\s*\(\s*255\s*,\s*0\s*,\s*0\s*\))/gi,
        'background-color: transparent',
    );
    result = result?.replace(
        /background:\s*(?:red|#ff0000|#f00|rgb\s*\(\s*255\s*,\s*0\s*,\s*0\s*\))/gi,
        'background: transparent',
    );
    result = result?.replace(
        /color:\s*(?:red|#ff0000|#f00|rgb\s*\(\s*255\s*,\s*0\s*,\s*0\s*\))/gi,
        'color: #434A54',
    );
    result = result?.replace(
        /color:\s*(?:white|#fff(?:fff)?|#ffffff|rgb\s*\(\s*255\s*,\s*255\s*,\s*255\s*\))/gi,
        'color: #434A54',
    );
    return result;
};

export const finalizeNotes = (html) => {
    let result = html || '';
    result = removeMniWhenApproved(result);
    result = removeBlueAndUnderlineFromNotes(result);
    return removeNoteRoundBrackets(result);
};

const isBlankQuillHtml = (html) => {
    if (!html || typeof html !== 'string') return true;
    const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
    return text.length === 0;
};

const pickFirstNonBlankHtml = (...candidates) => {
    for (const candidate of candidates) {
        if (!isBlankQuillHtml(candidate)) {
            return candidate;
        }
    }
    return '';
};

const resolveNarrativeId = (item) => (
    item?.NarrativeID ?? item?.NarrativeId ?? item?.narrativeID ?? item?.narrativeId ?? null
);

export { resolveNarrativeId };

export const parseMasterReportNarrative = (narrativeJson) => {
    try {
        const parsed = JSON.parse(narrativeJson || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const buildNarrativePrintFieldsFromRecord = (record) => ({
    CommentsDoc: record?.CommentsDoc || '',
    RedactedComment: record?.RedactedComment || '',
    UserCommentsDoc: pickFirstNonBlankHtml(
        record?.UserCommentsDoc,
        record?.userCommentsDoc,
        record?.LowerEditorCommentsDoc,
        record?.lowerEditorCommentsDoc,
    ),
    UserRedactedComment: pickFirstNonBlankHtml(
        record?.UserRedactedComment,
        record?.userRedactedComment,
        record?.UserRedactedC,
        record?.userRedactedC,
    ),
});

/**
 * Build per-narrative print field overrides from authoritative single-narrative records.
 * Master report JSON can duplicate CommentsDoc across rows; always fetch by NarrativeID.
 */
export const buildEnrichedNarrativeFieldOverrides = async ({
    narrativeJson,
    baseOverrides = {},
    fetchNarrativeById,
}) => {
    const items = parseMasterReportNarrative(narrativeJson);
    const overrides = { ...baseOverrides };

    await Promise.all(items.map(async (item) => {
        const narrativeId = resolveNarrativeId(item);
        if (narrativeId == null) return;

        const id = String(narrativeId);
        const liveOverride = baseOverrides[id] || {};

        if (typeof fetchNarrativeById !== 'function') {
            overrides[id] = { ...buildNarrativePrintFieldsFromRecord(item), ...liveOverride };
            return;
        }

        const record = await fetchNarrativeById(narrativeId);
        const source = record || item;

        overrides[id] = {
            ...buildNarrativePrintFieldsFromRecord(source),
            ...liveOverride,
        };
    }));

    return overrides;
};

export const mergeNarrativePrintFields = (item, narrativeFieldOverrides) => {
    if (!item || !narrativeFieldOverrides) return item;
    const narrativeId = resolveNarrativeId(item);
    if (narrativeId == null) return item;
    const overrides = narrativeFieldOverrides[String(narrativeId)];
    return overrides ? { ...item, ...overrides } : item;
};

/** Resolve lower-editor user notes from narrative payload (handles legacy field names). */
export const resolveUserNotesHtml = (item, isRedacted) => {
    const userCommentsDoc = pickFirstNonBlankHtml(
        item?.UserCommentsDoc,
        item?.userCommentsDoc,
        item?.LowerEditorCommentsDoc,
        item?.lowerEditorCommentsDoc,
    );
    const userRedactedComment = pickFirstNonBlankHtml(
        item?.UserRedactedComment,
        item?.userRedactedComment,
        item?.UserRedactedC,
        item?.userRedactedC,
    );

    if (isRedacted) {
        return userRedactedComment || userCommentsDoc;
    }

    return userCommentsDoc;
};
